/**
 * AskSession Constants
 *
 * This file contains constants used throughout the AskSession component.
 * Centralizing constants makes them easier to maintain and update.
 */

import { COLORS } from "@/components/common/Activities/ActivityMenu/styles/constants";

// ============= Storage Keys =============

/**
 * LocalStorage key prefix for ask session drafts.
 */
export const DRAFT_STORAGE_PREFIX = 'ask-draft-';

/**
 * LocalStorage key for message templates.
 */
export const TEMPLATES_STORAGE_KEY = 'ask-templates';

// ============= Timing Constants =============

/**
 * Interval in milliseconds for auto-saving drafts.
 */
export const DRAFT_AUTO_SAVE_INTERVAL_MS = 2000;

/**
 * Delay in milliseconds before restoring draft content.
 * Ensures component is fully mounted before setting content.
 */
export const DRAFT_RESTORE_DELAY_MS = 100;

/**
 * Duration in milliseconds to highlight a message after navigation.
 */
export const MESSAGE_HIGHLIGHT_DURATION_MS = 2000;

/**
 * Shown in chat when the user stops an in-flight assistant response.
 */
export const GENERATION_CANCELLED_MESSAGE = 'Generation cancelled';

/**
 * Delay in milliseconds between revealing each word in the assistant typing effect.
 */
export const STREAMING_WORD_DELAY_MS = 35;

// ============= Text Constants =============

/**
 * Maximum length for synopsis preview before truncation.
 */
export const MAX_SYNOPSIS_PREVIEW_LENGTH = 100;

/**
 * Maximum length for pinned message preview.
 */
export const MAX_PINNED_MESSAGE_PREVIEW_LENGTH = 60;

/**
 * Default synopsis truncation length.
 */
export const DEFAULT_SYNOPSIS_TRUNCATION_LENGTH = 100;

// ============= User Colors =============

/**
 * Color palette for user avatars.
 * Ensures consistent colors across the application.
 */
export const USER_COLOR_PALETTE = [
  '#EBB905', // yellow
  '#E6503C', // red
  '#8C5A19', // brown
  '#4169C8', // blue
  '#1E6450', // navy
  '#9B9696', // gray
  '#B4CD5A', // green
  '#1E6450', // dark green
] as const;

/**
 * Color for current user messages.
 */
export const CURRENT_USER_COLOR = '#82B9FA'; // Blue

/**
 * Color for assistant messages.
 */
export const ASSISTANT_COLOR = '#B95AFF'; // Green

// ============= Session Constants =============

/**
 * Default session title prefix.
 */
export const SESSION_TITLE_PREFIX = 'askMessage';

// ============= Keyboard Shortcuts =============

/**
 * Keyboard shortcut keys used in the application.
 */
export const KEYBOARD_SHORTCUTS = {
  FOCUS_INPUT: 'k',
  SEARCH: 'f',
  HELP: '/',
  TEMPLATES: 't',
  ESCAPE: 'Escape',
  ENTER: 'Enter',
} as const;

/**
 * Modifier keys for keyboard shortcuts.
 */
export const MODIFIER_KEYS = {
  CTRL: 'Ctrl',
  CMD: '⌘',
} as const;

// ============= Commands =============

/**
 * Available command templates for the enhanced text area.
 */
export const COMMAND_TEMPLATES = [
  {
    id: 'summarize',
    name: 'Summarize',
    type: 'command' as const,
    promptTemplate: 'Please summarize the following document: {document}',
    requiresDocument: true,
  },
  {
    id: 'extract',
    name: 'Extract Key Points',
    type: 'command' as const,
    promptTemplate: 'Extract the key points from: {document}',
    requiresDocument: true,
  },
  {
    id: 'compare',
    name: 'Compare Documents',
    type: 'command' as const,
    promptTemplate: 'Compare the following documents: {document}',
    requiresDocuments: 2,
  },
  {
    id: 'analyze',
    name: 'Analyze',
    type: 'command' as const,
    promptTemplate: 'Analyze the following document: {document}',
    requiresDocument: true,
  },
];
